/**
 * The `sic_codes` DISPLAY preset (formats.ts) — reading a classification-code answer back to
 * the person who gave it.
 *
 * The field holds what the register holds: a comma-separated list of 5-digit codes, and that
 * is exactly what is submitted. Nothing here rewrites, reorders, de-duplicates or filters the
 * answer; it derives a parallel, read-only view of it, so a code can be shown with what it
 * means without the meaning ever reaching the payload.
 *
 * TWO DISCIPLINES, both deliberate:
 *
 *  - An UNKNOWN code renders VERBATIM and is never dropped and never flagged. The published
 *    classification is revised without notice and an answer may predate this table, so a code
 *    this module cannot describe is a gap in the table, not a wrong answer. (The same rule the
 *    register connector's code→label map keeps for company types.)
 *  - The lookup is OWN-PROPERTY only. The value is free text a subject can type, and a plain
 *    index into a frozen object literal would happily return `Object.prototype`'s members for
 *    inputs like `constructor` or `toString` — a function where a description belongs.
 */
import { SIC_CODE_DESCRIPTIONS } from "./sicCodes.generated.js";

/**
 * How a multi-code answer is written. This is the `GROUP_LIST_SEPARATOR` (formModel.ts) the
 * register autofill joins with — but read back tolerantly rather than by exact match, because
 * the same field is typed by hand: "62012,70229", "62012, 70229" and "62012, 70229," are all
 * the same three-or-fewer answers.
 */
const SEPARATOR = ",";

/** One entered code, with its published description when this table carries one. */
export interface DescribedSicCode {
  code: string;
  description?: string;
}

/** The description for one code, or `undefined` — own properties only (see the header). */
function describeOne(code: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(SIC_CODE_DESCRIPTIONS, code)) return undefined;
  const description = SIC_CODE_DESCRIPTIONS[code];
  return typeof description === "string" ? description : undefined;
}

/**
 * Every code in the value, in the order it was entered, each with its description when known.
 * Blank entries (a trailing comma, a double comma, stray whitespace) are dropped; an empty or
 * whitespace-only value yields zero entries.
 */
export function describeSicCodes(value: string): ReadonlyArray<DescribedSicCode> {
  if (typeof value !== "string") return [];
  return value
    .split(SEPARATOR)
    .map((part) => part.trim())
    .filter((code) => code.length > 0)
    .map((code) => ({ code, description: describeOne(code) }));
}

/** The em dash that joins a code to its description — one place, so every surface agrees. */
export const SIC_DESCRIPTION_JOINER = ": "; // 0.43.0: no em dash in what clients and staff read; the engine twin is FormReferenceTables.DescriptionJoiner

/** One entry as one line: `<code> — <description>`, or bare `<code>` when undescribed. */
export function sicCodeLine(entry: DescribedSicCode): string {
  return entry.description ? `${entry.code}${SIC_DESCRIPTION_JOINER}${entry.description}` : entry.code;
}

/**
 * The whole answer as display lines — what a field row shows under the input and what the
 * review step reads back, so the answer confirmed reads the same as the answer typed.
 */
export function sicCodeLines(value: string): string[] {
  return describeSicCodes(value).map(sicCodeLine);
}
